import { c as _c } from "react/compiler-runtime";
import { ProductReviewFeedbackSubmitStatus, ProductReviewFeedbackVote } from '@spa/generated/graphql/graphql';
import { ReactElement, memo, useReducer, useState } from 'react';
import { hasSubmittedReviewFeedback, setReviewFeedbackSubmitted } from './bazaarvoiceReviewFeedbackStorage';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import logger from '@spa/lib/logger';
import styles from './bazaarvoice-review-feedback-row.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useProductReviewsAPI from '@spa/lib/react/hooks/useProductReviewsAPI';
export interface Props {
  readonly reviewId: string;
  readonly feedbackEligible: boolean;
  readonly positiveCount: number;
  readonly negativeCount: number;
}
interface VoteState {
  readonly positiveCount: number;
  readonly negativeCount: number;
  readonly hasVoted: boolean;
  readonly isSubmitting: boolean;
}
type VoteAction = {
  readonly type: 'submitting';
} | {
  readonly type: 'succeeded';
  readonly vote: ProductReviewFeedbackVote;
} | {
  readonly type: 'failed';
}
// Another browser tab already recorded a vote for this review -- reflect that locally without
// incrementing a count this tab never actually caused.
| {
  readonly type: 'synced';
};
function voteReducer(state: VoteState, action: VoteAction): VoteState {
  switch (action.type) {
    case 'submitting':
      return {
        ...state,
        isSubmitting: true
      };
    case 'succeeded':
      return {
        ...state,
        isSubmitting: false,
        hasVoted: true,
        positiveCount: action.vote === ProductReviewFeedbackVote.POSITIVE ? state.positiveCount + 1 : state.positiveCount,
        negativeCount: action.vote === ProductReviewFeedbackVote.NEGATIVE ? state.negativeCount + 1 : state.negativeCount
      };
    case 'failed':
      return {
        ...state,
        isSubmitting: false
      };
    case 'synced':
      return {
        ...state,
        isSubmitting: false,
        hasVoted: true
      };
  }
}
interface ReportState {
  readonly hasReported: boolean;
  readonly isSubmitting: boolean;
}
type ReportAction = {
  readonly type: 'submitting';
} | {
  readonly type: 'succeeded';
} | {
  readonly type: 'failed';
}
// Another browser tab already recorded a report for this review.
| {
  readonly type: 'synced';
};
function reportReducer(state: ReportState, action: ReportAction): ReportState {
  switch (action.type) {
    case 'submitting':
      return {
        ...state,
        isSubmitting: true
      };
    case 'succeeded':
      return {
        ...state,
        isSubmitting: false,
        hasReported: true
      };
    case 'failed':
      return {
        ...state,
        isSubmitting: false
      };
    case 'synced':
      return {
        ...state,
        isSubmitting: false,
        hasReported: true
      };
  }
}
function ThumbDownIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles["bazaarvoice-review-feedback-row__icon"]}><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ThumbUpIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles["bazaarvoice-review-feedback-row__icon"]}><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function BazaarvoiceReviewFeedbackRowComponent(t0) {
  const $ = _c(54);
  const {
    reviewId,
    feedbackEligible,
    positiveCount: initialPositiveCount,
    negativeCount: initialNegativeCount
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("bazaarvoice-review-section");
  const {
    submitProductReviewHelpfulnessFeedback,
    reportProductReview
  } = useProductReviewsAPI();
  let t1;
  if ($[0] !== initialNegativeCount || $[1] !== initialPositiveCount || $[2] !== reviewId) {
    t1 = {
      reviewId,
      positiveCount: initialPositiveCount,
      negativeCount: initialNegativeCount
    };
    $[0] = initialNegativeCount;
    $[1] = initialPositiveCount;
    $[2] = reviewId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [voteState, dispatchVote] = useReducer(voteReducer, t1, _temp);
  const [reportState, dispatchReport] = useReducer(reportReducer, reviewId, _temp2);
  const [hasError, setHasError] = useState(false);
  let t2;
  if ($[4] !== reviewId || $[5] !== submitProductReviewHelpfulnessFeedback || $[6] !== voteState.hasVoted || $[7] !== voteState.isSubmitting) {
    t2 = async vote => {
      if (voteState.isSubmitting) {
        return;
      }
      if (voteState.hasVoted) {
        return;
      }
      if (hasSubmittedReviewFeedback(reviewId, "helpfulness")) {
        dispatchVote({
          type: "synced"
        });
        return;
      }
      dispatchVote({
        type: "submitting"
      });
      setHasError(false);
      ;
      try {
        const result = await submitProductReviewHelpfulnessFeedback(reviewId, vote);
        if (result.status === ProductReviewFeedbackSubmitStatus.SUCCESS) {
          setReviewFeedbackSubmitted(reviewId, "helpfulness");
          dispatchVote({
            type: "succeeded",
            vote
          });
        } else {
          dispatchVote({
            type: "failed"
          });
          setHasError(true);
        }
      } catch (t3) {
        const error = t3;
        logger.error(error, "Error submitting product review helpfulness feedback");
        dispatchVote({
          type: "failed"
        });
        setHasError(true);
      }
    };
    $[4] = reviewId;
    $[5] = submitProductReviewHelpfulnessFeedback;
    $[6] = voteState.hasVoted;
    $[7] = voteState.isSubmitting;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleVote = t2;
  let t3;
  if ($[9] !== reportProductReview || $[10] !== reportState.hasReported || $[11] !== reportState.isSubmitting || $[12] !== reviewId) {
    t3 = async () => {
      if (reportState.isSubmitting) {
        return;
      }
      if (reportState.hasReported) {
        return;
      }
      if (hasSubmittedReviewFeedback(reviewId, "report")) {
        dispatchReport({
          type: "synced"
        });
        return;
      }
      dispatchReport({
        type: "submitting"
      });
      setHasError(false);
      ;
      try {
        const result_0 = await reportProductReview(reviewId);
        if (result_0.status === ProductReviewFeedbackSubmitStatus.SUCCESS) {
          setReviewFeedbackSubmitted(reviewId, "report");
          dispatchReport({
            type: "succeeded"
          });
        } else {
          dispatchReport({
            type: "failed"
          });
          setHasError(true);
        }
      } catch (t4) {
        const error_0 = t4;
        logger.error(error_0, "Error reporting product review");
        dispatchReport({
          type: "failed"
        });
        setHasError(true);
      }
    };
    $[9] = reportProductReview;
    $[10] = reportState.hasReported;
    $[11] = reportState.isSubmitting;
    $[12] = reviewId;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleReport = t3;
  if (!feedbackEligible) {
    return null;
  }
  let t4;
  if ($[14] !== handleVote) {
    t4 = () => {
      handleVote(ProductReviewFeedbackVote.NEGATIVE);
    };
    $[14] = handleVote;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = voteState.hasVoted || voteState.isSubmitting;
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ThumbDownIcon />;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== formatMessage || $[18] !== voteState.negativeCount) {
    t7 = formatMessage("notHelpfulAction", {
      count: voteState.negativeCount
    });
    $[17] = formatMessage;
    $[18] = voteState.negativeCount;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t5 || $[22] !== t7) {
    t8 = <button type="button" className={styles["bazaarvoice-review-feedback-row__button"]} onClick={t4} disabled={t5}>{t6}{t7}</button>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== handleVote) {
    t9 = () => {
      handleVote(ProductReviewFeedbackVote.POSITIVE);
    };
    $[24] = handleVote;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const t10 = voteState.hasVoted || voteState.isSubmitting;
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ThumbUpIcon />;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== formatMessage || $[28] !== voteState.positiveCount) {
    t12 = formatMessage("helpfulAction", {
      count: voteState.positiveCount
    });
    $[27] = formatMessage;
    $[28] = voteState.positiveCount;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t10 || $[31] !== t12 || $[32] !== t9) {
    t13 = <button type="button" className={styles["bazaarvoice-review-feedback-row__button"]} onClick={t9} disabled={t10}>{t11}{t12}</button>;
    $[30] = t10;
    $[31] = t12;
    $[32] = t9;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = classNames(styles["bazaarvoice-review-feedback-row__button"], styles["bazaarvoice-review-feedback-row__button--report"]);
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== handleReport) {
    t15 = () => {
      handleReport();
    };
    $[35] = handleReport;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const t16 = reportState.hasReported || reportState.isSubmitting;
  const t17 = reportState.hasReported ? "reportedAction" : "reportAction";
  let t18;
  if ($[37] !== formatMessage || $[38] !== t17) {
    t18 = formatMessage(t17);
    $[37] = formatMessage;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t15 || $[41] !== t16 || $[42] !== t18) {
    t19 = <button type="button" className={t14} onClick={t15} disabled={t16}>{t18}</button>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t13 || $[45] !== t19 || $[46] !== t8) {
    t20 = <div className={styles["bazaarvoice-review-feedback-row"]}>{t8}{t13}{t19}</div>;
    $[44] = t13;
    $[45] = t19;
    $[46] = t8;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== formatMessage || $[49] !== hasError) {
    t21 = hasError && <p className={styles["bazaarvoice-review-feedback-row__error"]} role="alert">{formatMessage("feedbackErrorMessage")}</p>;
    $[48] = formatMessage;
    $[49] = hasError;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t20 || $[52] !== t21) {
    t22 = <>{t20}{t21}</>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  return t22;
}
function _temp2(id) {
  return {
    hasReported: hasSubmittedReviewFeedback(id, "report"),
    isSubmitting: false
  };
}
function _temp(init) {
  return {
    positiveCount: init.positiveCount,
    negativeCount: init.negativeCount,
    hasVoted: hasSubmittedReviewFeedback(init.reviewId, "helpfulness"),
    isSubmitting: false
  };
}
export default memo(BazaarvoiceReviewFeedbackRowComponent);